class TenSecondVideoPreview extends HTMLElement {
  previewDuration = 10;

  media = null;
  frame = null;
  playButton = null;
  closeButton = null;
  lightbox = null;
  videoType = null;
  playerId = null;
  previewSource = '';
  isFullVideo = false;
  hasReset = false;

  connectedCallback() {
    this.media = this.querySelector(
      '.ten-second-video-preview__media'
    );

    this.frame = this.querySelector(
      '.ten-second-video-preview__frame'
    );

    this.playButton = this.querySelector(
      '.ten-second-video-preview__play-button'
    );

    this.closeButton = this.querySelector(
      '.ten-second-video-preview__close'
    );

    this.videoType = this.dataset.videoType;
    this.playerId = this.dataset.playerId;

    if (
      !this.media ||
      !this.frame ||
      !this.playButton ||
      !this.closeButton
    ) {
      return;
    }

    if (this.media instanceof HTMLIFrameElement) {
      this.previewSource = this.media.src;
    }

    this.playButton.addEventListener(
      'click',
      this.openFullVideo
    );

    this.closeButton.addEventListener(
      'click',
      this.closeFullVideo
    );

    if (this.videoType === 'uploaded') {
      this.setupUploadedPreview();
    }

    if (this.videoType === 'vimeo') {
      this.setupVimeoPreview();
    }
  }

  disconnectedCallback() {
    this.playButton?.removeEventListener(
      'click',
      this.openFullVideo
    );

    this.closeButton?.removeEventListener(
      'click',
      this.closeFullVideo
    );

    document.removeEventListener(
      'keydown',
      this.handleKeydown
    );

    window.removeEventListener(
      'resize',
      this.syncLightboxLayout
    );

    if (
      this.videoType === 'uploaded' &&
      this.media instanceof HTMLVideoElement
    ) {
      this.media.removeEventListener(
        'timeupdate',
        this.handleUploadedTimeUpdate
      );
    }

    if (this.videoType === 'vimeo') {
      window.removeEventListener(
        'message',
        this.handleVimeoMessage
      );
    }

    this.lightbox?.remove();

    document.body.classList.remove(
      'ten-second-video-lightbox-open'
    );
  }

  setupUploadedPreview() {
    if (!(this.media instanceof HTMLVideoElement)) {
      return;
    }

    this.media.muted = true;
    this.media.controls = false;
    this.media.loop = false;
    this.media.playsInline = true;

    this.media.removeEventListener(
      'timeupdate',
      this.handleUploadedTimeUpdate
    );

    this.media.addEventListener(
      'timeupdate',
      this.handleUploadedTimeUpdate
    );

    const playPromise = this.media.play();

    if (
      playPromise &&
      typeof playPromise.catch === 'function'
    ) {
      playPromise.catch(() => {
        /*
         * Some browsers may postpone autoplay until
         * the video is visible. The play button remains
         * available in that situation.
         */
      });
    }
  }

  handleUploadedTimeUpdate = () => {
    if (
      this.isFullVideo ||
      !(this.media instanceof HTMLVideoElement)
    ) {
      return;
    }

    if (
      this.media.currentTime >=
      this.previewDuration
    ) {
      this.media.currentTime = 0;
      this.media.play().catch(() => {});
    }
  };

  setupVimeoPreview() {
    window.addEventListener(
      'message',
      this.handleVimeoMessage
    );

    if (
      this.media instanceof
      HTMLIFrameElement
    ) {
      this.media.addEventListener(
        'load',
        this.subscribeToVimeoTimeUpdates,
        { once: true }
      );
    }
  }

  subscribeToVimeoTimeUpdates = () => {
    this.sendVimeoMessage({
      method: 'addEventListener',
      value: 'timeupdate'
    });
  };

  handleVimeoMessage = (event) => {
    if (
      this.isFullVideo ||
      !(
        this.media instanceof
        HTMLIFrameElement
      ) ||
      event.source !==
        this.media.contentWindow
    ) {
      return;
    }

    let message = event.data;

    if (typeof message === 'string') {
      try {
        message = JSON.parse(message);
      } catch {
        return;
      }
    }

    if (
      !message ||
      typeof message !== 'object'
    ) {
      return;
    }

    if (
      message.player_id &&
      this.playerId &&
      message.player_id !== this.playerId
    ) {
      return;
    }

    if (message.event === 'ready') {
      this.subscribeToVimeoTimeUpdates();
      return;
    }

    if (message.event !== 'timeupdate') {
      return;
    }

    const seconds =
      message.data?.seconds ??
      message.data?.currentTime ??
      0;

    if (seconds < 1) {
      this.hasReset = false;
    }

    if (
      seconds >= this.previewDuration &&
      !this.hasReset
    ) {
      this.hasReset = true;

      this.sendVimeoMessage({
        method: 'setCurrentTime',
        value: 0
      });

      this.sendVimeoMessage({
        method: 'play'
      });
    }
  };

  sendVimeoMessage(message) {
    if (
      !(
        this.media instanceof
        HTMLIFrameElement
      )
    ) {
      return;
    }

    this.media.contentWindow?.postMessage(
      JSON.stringify(message),
      '*'
    );
  }

  getHeaderSection() {
    return document
      .querySelector('#header-component')
      ?.closest('.header-section');
  }

  getVisibleSearchIcon() {
    const icons = Array.from(
      document.querySelectorAll(
        '#header-component .search-action svg'
      )
    );

    return icons.find((icon) => {
      const rectangle =
        icon.getBoundingClientRect();

      const styles =
        window.getComputedStyle(icon);

      return (
        rectangle.width > 0 &&
        rectangle.height > 0 &&
        styles.display !== 'none' &&
        styles.visibility !== 'hidden'
      );
    });
  }

  getVisibleNavigationTitle() {
    const titles = Array.from(
      document.querySelectorAll(
        '#header-component .artist-header-navigation__link'
      )
    );

    return titles.find((title) => {
      const rectangle =
        title.getBoundingClientRect();

      const styles =
        window.getComputedStyle(title);

      return (
        rectangle.width > 0 &&
        rectangle.height > 0 &&
        styles.display !== 'none' &&
        styles.visibility !== 'hidden'
      );
    });
  }

  getFrameAspectRatio() {
    const value = window
      .getComputedStyle(this)
      .getPropertyValue(
        '--size-style-aspect-ratio'
      )
      .trim();

    if (value.includes('/')) {
      const [width, height] = value
        .split('/')
        .map((part) =>
          Number.parseFloat(part)
        );

      if (width > 0 && height > 0) {
        return width / height;
      }
    }

    const numericValue =
      Number.parseFloat(value);

    return numericValue > 0
      ? numericValue
      : 16 / 9;
  }

  syncLightboxLayout = () => {
    if (
      !this.lightbox ||
      !this.isFullVideo
    ) {
      return;
    }

    const headerSection =
      this.getHeaderSection();

    const headerBottom = Math.max(
      0,
      Math.round(
        headerSection
          ?.getBoundingClientRect()
          .bottom || 0
      )
    );

    const availableHeight = Math.max(
      0,
      window.innerHeight - headerBottom
    );

    const navigationTitle =
      this.getVisibleNavigationTitle();

    const navigationRectangle =
      navigationTitle
        ?.getBoundingClientRect();

    const headerInnerBottomSpacing =
      navigationRectangle
        ? Math.max(
            20,
            headerBottom -
              navigationRectangle.bottom
          )
        : 32;

    const frameEdgeSpacing =
      window.innerWidth < 750
        ? 20
        : headerInnerBottomSpacing + 12;

    const verticalSpacing =
      frameEdgeSpacing * 2;

    const horizontalWidth = Math.max(
      0,
      window.innerWidth -
        frameEdgeSpacing * 2
    );

    const verticalWidth = Math.max(
      0,
      (availableHeight -
        verticalSpacing) *
        this.getFrameAspectRatio()
    );

    const frameWidth = Math.min(
      horizontalWidth,
      verticalWidth
    );

    this.lightbox.style.top =
      `${headerBottom}px`;

    this.frame.style.width =
      `${frameWidth}px`;

    this.frame.style.aspectRatio =
      String(this.getFrameAspectRatio());

    const searchIcon =
      this.getVisibleSearchIcon();

    const searchIconRight =
      searchIcon
        ?.getBoundingClientRect()
        .right;

    const closeRight = Number.isFinite(
      searchIconRight
    )
      ? Math.max(
          0,
          window.innerWidth -
            searchIconRight
        )
      : 40;

    this.lightbox.style.setProperty(
      '--video-lightbox-close-right',
      `${closeRight}px`
    );

    window.requestAnimationFrame(() => {
      if (
        !this.lightbox ||
        !this.isFullVideo
      ) {
        return;
      }

      const frameRectangle =
        this.frame.getBoundingClientRect();

      const lightboxRectangle =
        this.lightbox.getBoundingClientRect();

      this.closeButton.style.top = '0px';

      const buttonRectangle =
        this.closeButton
          .getBoundingClientRect();

      const labelRectangle =
        this.closeButton
          .querySelector(
            '.ten-second-video-preview__close-label'
          )
          ?.getBoundingClientRect();

      const labelOffset = labelRectangle
        ? labelRectangle.top -
          buttonRectangle.top
        : 0;

      const closeTop = Math.max(
        0,
        frameRectangle.top -
          lightboxRectangle.top -
          labelOffset
      );

      this.closeButton.style.top =
        `${closeTop}px`;
    });
  };

  openFullVideo = () => {
    if (
      this.isFullVideo ||
      !this.media ||
      !this.frame
    ) {
      return;
    }

    this.isFullVideo = true;

    this.classList.add(
      'is-full-video'
    );

    this.playButton.hidden = true;

    if (
      this.videoType === 'uploaded' &&
      this.media instanceof HTMLVideoElement
    ) {
      this.media.removeEventListener(
        'timeupdate',
        this.handleUploadedTimeUpdate
      );

      this.media.controls = true;

      this.media.loop =
        this.dataset.fullLoop === 'true';

      this.media.muted = false;
      this.media.currentTime = 0;

      this.media.play().catch(() => {});
      return;
    }

    if (
      this.media instanceof
      HTMLIFrameElement
    ) {
      const fullVideoSource =
        this.media.dataset.fullSrc;

      if (fullVideoSource) {
        this.media.src =
          fullVideoSource;
      }
    }
  };

  closeFullVideo = () => {
    if (!this.isFullVideo) {
      return;
    }

    this.isFullVideo = false;

    this.classList.remove(
      'is-full-video'
    );

    document.removeEventListener(
      'keydown',
      this.handleKeydown
    );

    window.removeEventListener(
      'resize',
      this.syncLightboxLayout
    );

    if (
      this.videoType === 'uploaded' &&
      this.media instanceof HTMLVideoElement
    ) {
      this.media.pause();
      this.media.controls = false;
      this.media.loop = false;
      this.media.muted = true;
      this.media.currentTime = 0;
    }

    if (
      this.media instanceof
        HTMLIFrameElement &&
      this.previewSource
    ) {
      if (this.videoType === 'vimeo') {
        this.media.addEventListener(
          'load',
          this.subscribeToVimeoTimeUpdates,
          { once: true }
        );
      }

      this.media.src =
        this.previewSource;
    }

    this.frame.style.removeProperty(
      'width'
    );

    this.frame.style.removeProperty(
      'aspect-ratio'
    );

    this.closeButton.style.removeProperty(
      'top'
    );

    this.closeButton.hidden = true;

    this.append(
      this.frame,
      this.closeButton
    );

    this.lightbox?.remove();
    this.lightbox = null;

    document.body.classList.remove(
      'ten-second-video-lightbox-open'
    );

    this.playButton.hidden = false;
    this.hasReset = false;

    if (this.videoType === 'uploaded') {
      this.setupUploadedPreview();
    }
  };

  handleKeydown = (event) => {
    if (event.key === 'Escape') {
      this.closeFullVideo();
    }
  };
}

if (
  !customElements.get(
    'ten-second-video-preview'
  )
) {
  customElements.define(
    'ten-second-video-preview',
    TenSecondVideoPreview
  );
}